import { useState } from "react";
import { Star, Check } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { ENTITY_TYPES } from "../../data/services";

const field = "w-full bg-transparent border-b border-ink/25 focus:border-ink outline-none py-3 text-ink placeholder:text-ink/50 transition-colors";
const EMPTY = { name: "", designation: "", company: "", entity_type: "Stock Broker", rating: 5, message: "" };

export const ReviewForm = () => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/reviews", f);
      setDone(true);
      setF(EMPTY);
      toast.success("Thank you! Your review has been submitted for approval.");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div data-testid="review-success" className="py-16 text-center">
        <span className="mx-auto grid place-items-center w-16 h-16 rounded-full bg-ink text-[#93a8e8]"><Check className="w-8 h-8" /></span>
        <p className="mt-6 font-display text-3xl font-bold">Thank you.</p>
        <p className="mt-2 text-ink/70">Your review will appear on the site once approved.</p>
        <button data-testid="review-write-another" onClick={() => setDone(false)} className="mt-6 text-[var(--brass-deep)] underline underline-offset-4">Write another</button>
      </div>
    );
  }

  return (
    <form data-testid="review-form" onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
      <input data-testid="review-name-input" required minLength={2} placeholder="Your name *" className={field} value={f.name} onChange={set("name")} />
      <input data-testid="review-designation-input" placeholder="Designation" className={field} value={f.designation} onChange={set("designation")} />
      <input data-testid="review-company-input" placeholder="Organisation" className={field} value={f.company} onChange={set("company")} />
      <select data-testid="review-entity-select" className={field} value={f.entity_type} onChange={set("entity_type")}>
        {ENTITY_TYPES.map((t) => <option key={t}>{t}</option>)}
      </select>
      <div className="sm:col-span-2 flex items-center gap-4 py-3">
        <span className="text-ink/70 text-sm">Your rating</span>
        <span className="flex gap-1" data-testid="review-rating">
          {[1, 2, 3, 4, 5].map((i) => (
            <button type="button" key={i} data-testid={`review-star-${i}`} onClick={() => setF({ ...f, rating: i })} className="p-0.5 hover:scale-125 transition-transform">
              <Star className={`w-6 h-6 ${i <= f.rating ? "fill-[var(--brass)] text-[var(--brass)]" : "text-ink/30"}`} />
            </button>
          ))}
        </span>
      </div>
      <textarea data-testid="review-message-input" required minLength={10} rows={4} placeholder="Share your experience working with us *" className={`${field} sm:col-span-2 resize-none`} value={f.message} onChange={set("message")} />
      <div className="sm:col-span-2 pt-4">
        <button data-testid="submit-review-button" disabled={busy} className="rounded-full bg-ink text-paper px-8 py-4 font-semibold hover:bg-brass hover:text-paper transition-colors duration-300 disabled:opacity-60">
          {busy ? "Submitting…" : "Submit review"}
        </button>
      </div>
    </form>
  );
};
